import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { useDataProvider } from "react-admin";
import useAiInsight from "../../../Components/AiAssistant/useAiInsight";
import { buildAiBaseParams } from "../../../Components/AiAssistant/buildAiBaseParams";
import { Typography } from "@material-ui/core";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

const CockpitFleetType = ({ filterParams }) => {
  const dataProvider = useDataProvider();

  const [chartData, setChartData] = useState([] as any);

  const resourceEndpoint = "pbhrequestorders/cockpit/fleettype";

  const COLORS = [
    "#1f77b4",
    "#ff7f0e",
    "#2ca02c",
    "#d62728",
    "#9467bd",
    "#8c564b",
    "#e377c2",
    "#7f7f7f",
    "#bcbd22",
    "#17becf",
    "#aec7e8",
    "#ffbb78",
    "#98df8a",
    "#ff9896",
    "#c5b0d5",
  ];

  const insightParams = useMemo(() => {
    if (!chartData.length) return null;
    const total = chartData.reduce((s: number, d: any) => s + d.value, 0);
    const dominant = chartData.find((d: any) => d.name !== "Others");
    const fleetTypeData = chartData
      .filter((d: any) => d.name !== "Others")
      .map((d: any) => `${d.name}:${d.value}(${((d.value / total) * 100).toFixed(1)}%)`)
      .join(",");
    return {
      ...buildAiBaseParams(filterParams),
      totalAircraftCount: total.toString(),
      dominantFleetType: dominant?.name ?? "",
      fleetTypeData,
    };
  }, [filterParams, chartData]);
  useAiInsight("cockpitfleettype", insightParams);

  useEffect(() => {
    GetChartData();
  }, [filterParams]);

  const GetChartData = async () => {
    await dataProvider
      .getList(resourceEndpoint, {
        pagination: { page: 1, perPage: 999999 },
        sort: { field: "id", order: "ASC" },
        filter: filterParams,
      })
      .then(({ data }) => {
        // Transform and sort data for pie chart
        let transformedData = data.map((item, index) => ({
          name: item.fleetType || `Fleet ${index + 1}`,
          value: item.count || item.value || 1,
          originalData: item,
        }));

        // Sort by value descending and group small values
        transformedData.sort((a, b) => b.value - a.value);

        // Group small values (less than 2% of total) into "Others"
        const total = transformedData.reduce((sum, item) => sum + item.value, 0);
        const threshold = total * 0.02; // 2% threshold

        const mainItems = transformedData.filter(item => item.value >= threshold);
        const smallItems = transformedData.filter(item => item.value < threshold);

        if (smallItems.length > 0) {
          const othersSum = smallItems.reduce((sum, item) => sum + item.value, 0);
          mainItems.push({
            name: "Others",
            value: othersSum,
            originalData: { id: "others", fleetType: "Others", count: othersSum },
          });
        }

        setChartData(mainItems);
      })
      .catch(error => {
        console.log(error);
      });
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    // Only show label if percentage is above 3%
    if (percent < 0.03) return null;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30; // Position labels outside the pie chart
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <g>
        <text
          x={x}
          y={y - 8}
          fill="black"
          textAnchor={x > cx ? "start" : "end"}
          dominantBaseline="central"
          fontSize="12"
          fontWeight="bold"
        >
          {name}
        </text>
        <text
          x={x}
          y={y + 8}
          fill="black"
          textAnchor={x > cx ? "start" : "end"}
          dominantBaseline="central"
          fontSize="14"
          fontWeight="bold"
        >
          {`${(percent * 100).toFixed(1)}%`}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            padding: "8px 12px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold", color: data.color }}>{data.name}</p>
          <p style={{ margin: 0, fontSize: "12px" }}>Count: {data.value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Typography gutterBottom variant="h5" component="h2" align="center">
        Aircraft Types
      </Typography>
      <div style={{ border: "1px solid black", width: 500, height: 400 }}>
        <PieChart width={500} height={400}>
          <Pie
            data={chartData}
            cx={250}
            cy={200}
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
            strokeWidth={1}
            stroke="#fff"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </div>
    </>
  );
};

export default CockpitFleetType;
